import { PlusIcon, SparklesIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { IProjectAsset, AssetType } from '@packages/global';
import { useTranslation } from 'react-i18next';
import SubjectCard from './SubjectCard';

interface SubjectGroupProps {
  title: string;
  icon: React.ReactNode;
  subjects: IProjectAsset[];
  assetType: AssetType;
  onAdd: () => void;
  onImport?: () => void;
  onEdit: (asset: IProjectAsset) => void;
  onDelete: (asset: IProjectAsset) => void;
  onPreview: (url: string) => void;
  onGenerate?: (asset: IProjectAsset) => void;
  onUpload?: (asset: IProjectAsset) => void;
  batchGenerating?: boolean;
  onBatchGenerate?: () => void;
}

export default function SubjectGroup({
  title, icon, subjects, assetType,
  onAdd, onImport, onEdit, onDelete, onPreview,
  onGenerate, onUpload,
  batchGenerating, onBatchGenerate
}: SubjectGroupProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  const assetsWithoutImage = subjects.filter(a => !a.conceptImageUrl && !a.frontViewUrl);
  const hasTargets = assetsWithoutImage.length > 0;

  const assetTypeNameMap: Record<AssetType, string> = {
    [AssetType.CHARACTER]: t('subject.character'),
    [AssetType.PROP]: t('subject.prop'),
    [AssetType.SCENE]: t('subject.scene'),
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-base font-semibold text-gray-50">{title}</h3>
          <span className="text-sm text-gray-400">({subjects.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {onBatchGenerate && (
            <button
              onClick={onBatchGenerate}
              disabled={batchGenerating || !hasTargets}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-primary/40 hover:border-primary/70 hover:bg-primary/10 text-primary rounded-lg text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-primary/40 disabled:hover:bg-transparent cursor-pointer"
              title={!hasTargets ? t('subject.allHaveImages') : undefined}
            >
              {batchGenerating ? (
                <div className="w-3.5 h-3.5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              ) : (
                <SparklesIcon className="w-3.5 h-3.5" />
              )}
              {batchGenerating ? t('common:generating') : hasTargets ? t('subject.generateCount', { count: assetsWithoutImage.length }) : t('common:generate')}
            </button>
          )}
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-border-default hover:bg-bg-elevated text-gray-300 hover:text-gray-50 rounded-lg text-sm transition-colors cursor-pointer"
          >
            <PlusIcon className="w-4 h-4" />
            {t('common:add')}
          </button>
          {onImport && (
            <button
              onClick={onImport}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-border-default hover:bg-bg-elevated text-gray-300 hover:text-gray-50 rounded-lg text-sm transition-colors cursor-pointer"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              {t('common:import')}
            </button>
          )}
        </div>
      </div>

      {/* Cards */}
      {subjects.length === 0 ? (
        <div className="flex items-center justify-center py-6 text-gray-400 bg-bg-surface rounded-xl border border-border-subtle">
          <p className="text-sm">{t('subject.emptyType', { type: assetTypeNameMap[assetType] })}</p>
        </div>
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
          {subjects.map((subject) => (
            <SubjectCard
              key={subject.id}
              subject={subject}
              onEdit={() => onEdit(subject)}
              onDelete={() => onDelete(subject)}
              onPreview={onPreview}
              onGenerate={onGenerate}
              onUpload={onUpload}
            />
          ))}
        </div>
      )}
    </div>
  );
}
